import { getFileBuffer } from '@/vendor/pleased-sdk/util';
import { Base64 } from 'js-base64';
import * as ApiModel from './models.pleased';
import { DeflectionLog } from '../bot/enum/DeflectionLog';
import { TransStatement } from './model';
import { TransactionTypeName } from '../transaction-utils';
import * as env from '@/common/environment';
import { ErrorCode, AiThresholdLimitError, RequestLimitError } from '../error';
import { AI_EVENTS, analytics, CRITICAL_EVENTS } from '../logs';
import {
  FALLBACK_OPEN_REQUEST_DESCRIPTION,
  FALLBACK_OPEN_REQUEST_DESCRIPTION_LENGTH,
  FALLBACK_OPEN_REQUEST_SUBJECT,
} from '../openRequest';
import { httpClient, SUCCESS_CODE, throwIfError, setConfig } from './http-client';
import type { PleaseApiConfig } from './http-client';
import { logErrorEvents } from './logger';
import { ChatTriggerMatchSource } from '../bot/flow-frameworks/chat-trigger';
import { looselyCheckIfMaintenanceIsOngoing } from '../MaintenanceLib';

// Re-export for backwards compatibility
export { httpClient, setConfig, logErrorEvents };
export type { PleaseApiConfig };
export { ApiModel as Model };

/**
 * Pooled widget runtime config (Firebase, default brand, etc.). Requires `setConfig` base URL and brand header first.
 */
export async function getChatWidgetClientConfig(id: number, origin: string): Promise<ApiModel.ClientConfig> {
  const response = await httpClient.get<ApiModel.ClientConfigResponse>(
    `/api/p/chat-widget-configs/${id}?clientOrigin=${origin}`
  );
  throwIfError(response);
  return response.data.data;
}

/**
 * Get a list of categories to show when the chatbot starts.
 * @returns The list of categories to show.
 */
export async function getBrandConfig({ brandCode }: { brandCode?: string } = {}) {
  brandCode = brandCode || httpClient.defaults.headers['brand-subdomain'];
  const response = await httpClient.get<ApiModel.BrandConfigResponse>(`/api/v1/chat-widget/brand/${brandCode}`);

  if (response.data.code !== SUCCESS_CODE) {
    analytics.trackCriticalEvent({
      name: CRITICAL_EVENTS.CHATBOT_FAILED_LOAD_BRAND_CONFIG,
      metadata: {
        error: response.data.message,
      },
    });
  }

  const isInMaintenanceWindow = await looselyCheckIfMaintenanceIsOngoing();
  if (isInMaintenanceWindow) {
    // disable AI
    response.data.data.brandConfig.aiEnabled = false;
  }

  return response.data;
}

/**
 * Minimal connectivity / RTT probe for client checks (e.g. voice readiness).
 * Expect a tiny, fast response; same auth/brand headers as other API calls apply via httpClient.
 */
export async function ping(): Promise<void> {
  await httpClient.get('/api/v1/chat-widget/ping', {
    headers: {
      'Cache-Control': 'no-cache',
    },
    params: { _: Date.now() },
    timeout: 12000,
  });
}

/**
 * Get sections/details of the given category.
 */
export async function getCategoryDetails({ categoryId }: { categoryId: string | number }) {
  const response = await httpClient.get<ApiModel.CategoryDetailResponse>(`/api/v1/chat-widget/category/${categoryId}`);
  throwIfError(response);
  response.data.data.noneOfTheAboveConfig = response.data.data.noneOfTheAboveConfig || {
    noneOfTheAbove: false,
  };

  // AI TODO: remove after BE/BO integration
  const { skipAi } = env.get('AI') || { skipAi: null };

  const skipAiForCategory = skipAi?.categoryIds?.includes(String(categoryId));

  if (skipAiForCategory) {
    response.data.data.categoryConfig.skipAiChat = true;
  }

  response.data.data.sections = response.data.data.sections.map((s) => {
    return { ...s, skipAiChat: s.skipAiChat ?? (skipAi?.sectionIds?.includes(String(s.id)) || skipAiForCategory) };
  });

  return response.data;
}
getCategoryDetails.cached = createCache(getCategoryDetails, ({ categoryId }) => localizeKey(categoryId));

/**
 * Get the subsections/articles in the given section.
 */
export async function getSectionDetails({ sectionId }: { sectionId: string | number }) {
  const response = await httpClient.get<ApiModel.SectionDetailResponse>(`/api/v1/chat-widget/section/${sectionId}`);

  // AI TODO: remove after BE/BO integration
  const { skipAi } = env.get('AI') || { skipAi: null };

  const skipAiForSection = skipAi?.sectionIds?.includes(String(sectionId));

  if (skipAiForSection) {
    response.data.data.sectionConfig.skipAiChat = true;
  }

  return response.data;
}
getSectionDetails.cached = createCache(getSectionDetails, ({ sectionId }) => localizeKey(sectionId));

/**
 * Get the subsections/articles in the given section.
 */
export async function getArticleDetails({ articleId }: { articleId: string | number }) {
  const response = await httpClient.get<ApiModel.ArticleDetailResponse>(`/api/v1/chat-widget/article/${articleId}`);
  throwIfError(response);

  return response.data;
}
getArticleDetails.cached = createCache(getArticleDetails, ({ articleId }) => localizeKey(articleId));

export async function getRelatedArticles({ articleId }: { articleId: string | number }) {
  const response = await httpClient.get<ApiModel.RelatedArticlesResponse>(
    `/api/v1/chat-widget/article/${articleId}/related`
  );
  throwIfError(response);
  return response.data;
}
getRelatedArticles.cached = createCache(getRelatedArticles, ({ articleId }) => localizeKey(articleId));

/**
 * Search for articles with the given query.
 */
export async function getArticleSearch({
  searchText,
  limit,
  offset,
}: {
  searchText: string;
  limit?: number;
  offset?: number;
}) {
  const response = await httpClient.get<ApiModel.ArticleSearchResponse>('/api/v1/chat-widget/article/search', {
    params: { searchText, limit, offset },
  });
  throwIfError(response);
  const searchResults = response.data;
  searchResults.data.map((x) => {
    x.title = x.title || x.titleTranslated;
  });

  return searchResults;
}
getArticleSearch.cached = createCache(getArticleSearch, ({ searchText, limit, offset }) =>
  localizeKey(`${searchText}=${limit}=${offset}`)
);

// TODO: use it once max-cache-size added to the cache, otherwise it will be a memory leak
// getArticleSearch.cached = createCache(getArticleSearch, ({ searchText }) => localizeKey(searchText));

/**
 * Get section config assigned to the given article.
 */
export async function getArticleParentConfig({
  articleId,
  getCategory = true,
}: {
  articleId: number | string;
  getCategory?: boolean;
}) {
  const response = await httpClient.get<ApiModel.ArticleParentConfigResponse>(
    `/api/v1/chat-widget/article/${articleId}/parent-config`,
    {
      params: {
        category_only: getCategory,
      },
    }
  );
  throwIfError(response);

  // AI TODO: remove after BE/BO integration
  const { skipAi } = env.get('AI') || { skipAi: null };

  // There might be no category config
  if (response.data.data && skipAi?.categoryIds?.includes(String(response.data.data.id))) {
    response.data.data.skipAiChat = true;
  }

  return response.data;
}
getArticleParentConfig.cached = createCache(getArticleParentConfig, ({ articleId, getCategory = true }) =>
  localizeKey(`${articleId}--${getCategory}`)
);

export async function getArticleRating({ articleId }: { articleId: number }) {
  const response = await httpClient.get<ApiModel.ArticleRatingResponse>('/api/v1/chat-widget/article/rate', {
    params: { articleId },
  });
  throwIfError(response);
  return response.data;
}

export async function rateArticle({ articleId, rating }: { articleId: number; rating: ApiModel.ArticleRate }) {
  const response = await httpClient.post<ApiModel.ArticleRatingResponse>('/api/v1/chat-widget/article/rate', {
    articleId,
    rating,
  });
  throwIfError(response);
  return response.data;
}

export async function saveArticleHit({ articleId, searchText }: { articleId: number; searchText: string }) {
  const response = await httpClient.post('/api/v1/chat-widget/article/save-hit', { articleId, searchText });
  throwIfError(response);
}

/**
 * Send deflection log with selected channel & overloaded channel.
 */
export async function sendDeflectionLog({ selectedChannel, overloadedChannel }: DeflectionLog) {
  const response = await httpClient.post<ApiModel.ApiResponse<unknown>>('/api/v1/chat-widget/log-deflection', {
    selectedChannel,
    overloadedChannel,
  });
  throwIfError(response);
  return response.data;
}

export async function trackArticle(params: ApiModel.ArticleMetrics) {
  const response = await httpClient.post('/api/v1/chat-widget/article/track-article', params);
  throwIfError(response);
}

/**
 * Sends the user entry page source (log)
 */
export async function sendEntryLog(params: ApiModel.EntryLogRequestBody) {
  const response = await httpClient.post('/api/v1/chatbot/log-entry', params);
  throwIfError(response);
  return response.data;
}

/**
 * Create a ticket with the given fields.
 * @param request The request body for the ticket.
 */
export async function createTicket(request: ApiModel.TicketCreateRequest) {
  if (!request.subject) {
    analytics.trackCriticalEvent({
      name: CRITICAL_EVENTS.OPEN_REQUEST_SUBJECT_MISSING,
    });
  }
  if (!request.description) {
    analytics.trackCriticalEvent({
      name: CRITICAL_EVENTS.OPEN_REQUEST_DESCRIPTION_MISSING,
    });
  }

  if (request.description && request.description.length <= FALLBACK_OPEN_REQUEST_DESCRIPTION_LENGTH) {
    analytics.trackCriticalEvent({
      name: CRITICAL_EVENTS.OPEN_REQUEST_SHORT_DESCRIPTION,
    });
  }

  const payload = {
    ...request,
    subject: request.subject || FALLBACK_OPEN_REQUEST_SUBJECT,
    description: request.description || FALLBACK_OPEN_REQUEST_DESCRIPTION,
  };

  const response = await httpClient.post<ApiModel.TicketCreateResponse>(
    '/api/v1/support/tickets/open-request',
    payload
  );

  if (response.data.code === ErrorCode.REQUESTS_LIMIT) {
    throw new RequestLimitError();
  }

  throwIfError(response);
  return response.data.data;
}

/**
 * Get a list of all ticket fields
 */
export async function getTicketFields() {
  const response = await httpClient.get<ApiModel.TicketFieldsResponse>(
    '/api/v1/support/ticket-fields/non-system-user-editable'
  );
  throwIfError(response);
  return response.data.data;
}

getTicketFields.cached = createCache(getTicketFields);

/**
 * Send OTP to email for guest ticket creation
 */
export async function sendOtpToEmail(email: string, guestData: ApiModel.GuestOtpRequest) {
  const response = await httpClient.post('/api/p/otp', {
    email,
    ...guestData,
  });
  throwIfError(response);
}

/**
 * Verify OTP and create guest ticket in one call
 */
export async function verifyOtpAndCreateTicket(request: ApiModel.GuestTicketCreateRequest) {
  const response = await httpClient.post<ApiModel.TicketCreateResponse>('/api/p/tickets/open-request-guest', request);
  throwIfError(response);
  return response.data.data;
}

/**
 * Get the position of the queue for the client.
 */
export async function getQueuePosition() {
  const response = await httpClient.get<ApiModel.QueuePositionResponse>('/api/v1/chat/client-queue-position');
  throwIfError(response);
  return response.data.data;
}

export async function getPleasedTokenFromSportyToken({
  countryCode,
  sportyToken,
  userCountry,
}: {
  countryCode: string;
  sportyToken: string;
  userCountry?: string;
}) {
  // Special case for Brazil launch
  const code =
    httpClient.defaults.headers['brand-subdomain'] === 'sportybetint' && userCountry === 'br' && countryCode === 'int'
      ? 'br'
      : countryCode;
  const response = await httpClient.get<ApiModel.UserInformationResponse>(
    `/api/v1/support/users/sportybet-user-token/${code}`,
    { headers: { 'sf-access-token': sportyToken } }
  );
  throwIfError(response);
  return { ...response.data.data };
}

/**
 * New WAP/WEB auth flow.
 * Exchange a host-generated JWE (from SB callback) for Pleased token + user metadata.
 *
 * Endpoint: GET /api/v1/support/users/end-user-token
 * Headers:
 * - X-PL-Auth: <JWE>
 * - brand-subdomain: <auto-set in httpClient config>
 * Query params:
 * - code: <country code>
 */
export async function getPleasedTokenFromEndUserJweToken({
  countryCode,
  jweToken,
  userCountry,
}: {
  countryCode: string;
  jweToken: string;
  userCountry?: string;
}) {
  // Special case for Brazil launch (keep behaviour consistent with sporty token exchange)
  const code =
    httpClient.defaults.headers['brand-subdomain'] === 'sportybetint' && userCountry === 'br' && countryCode === 'int'
      ? 'br'
      : countryCode;
  const response = await httpClient.get<ApiModel.UserInformationResponse>('/api/v1/support/users/end-user-token', {
    params: { code: code || undefined },
    headers: { 'X-PL-Auth': jweToken },
  });
  throwIfError(response);
  return { ...response.data.data };
}

export async function verifyAuthToken({ token }: { token: string }) {
  const response = await httpClient.get<ApiModel.UserInformationResponse>('/api/v1/support/users/end-user-token/', {
    headers: { 'X-PL-Auth': token },
  });
  throwIfError(response);
  return response.data?.data;
}

/**
 * Get a user token for guest user to perform call/chat activities.
 */
export async function getAnonymousToken() {
  const response = await httpClient.get<ApiModel.UserInformationResponse>('/api/v1/support/users/anonymous-token');
  throwIfError(response);
  return response.data.data;
}

export async function listTickets(params: ApiModel.TicketListQueryParams) {
  const response = await httpClient.get<ApiModel.TicketListResponse>('/api/v1/support/tickets', {
    params: { ...params, currentBrandOnly: true, activeConversations: true },
  });
  throwIfError(response);
  return response.data;
}

export async function getTicket(ticketId: number) {
  const response = await httpClient.get<ApiModel.TicketCreateResponse>(`/api/v1/support/tickets/${ticketId}`, {
    params: { activeConversations: true },
  });
  throwIfError(response);
  return response.data.data;
}

export async function getTicketComments(ticketId: number) {
  const response = await httpClient.get<ApiModel.ApiResponse<ApiModel.Comment[]>>(
    `/api/v1/support/tickets/${ticketId}/comments`
  );
  throwIfError(response);
  return response.data;
}

export async function addTicketComment({
  ticketId,
  comment,
  attachment = 0,
  sourceId = 1,
}: {
  ticketId: number;
  comment: string;
  attachment?: number;
  sourceId?: number;
}) {
  const response = await httpClient.post<ApiModel.ApiResponse<ApiModel.Comment>>(
    `/api/v1/support/tickets/${ticketId}/comment`,
    {
      comment,
      isPublic: true,
      attachmentIds: attachment ? [attachment] : [],
      sourceId,
    }
  );
  throwIfError(response);
  return response.data.data;
}

export async function uploadAttachment(file: File) {
  const fileBuffer = await getFileBuffer(file);
  const encodedFileName = Base64.encode(file.name);
  const headerParams = {
    'file-name': encodedFileName || 'unknown',
    'is-inline': false,
    'Content-Type': 'application/octet-stream',
    'base64-file-name': encodedFileName,
  };

  const response = await httpClient.post<ApiModel.AddAttachmentResponse>(
    '/api/v1/attachment/ticket_comment',
    fileBuffer,
    { headers: headerParams }
  );
  throwIfError(response);
  return response.data.data;
}

export async function addAttachmentComment({ ticketId, file }: { ticketId: number; file: File }) {
  const attachmentResponse = await uploadAttachment(file);
  const commentResponse = await addTicketComment({
    ticketId,
    comment: 'File Upload',
    attachment: attachmentResponse.id,
  });
  return { comment: commentResponse, attachment: attachmentResponse };
}

/**
 * Check if agents are online for voice chat.
 */
export async function areVoiceChatAgentsOnline() {
  const isMaintenanceOngoing = await looselyCheckIfMaintenanceIsOngoing();
  if (isMaintenanceOngoing) {
    // Disable voice chat in maintenance window as we won't be able to provide accurate info about agent availability, and to avoid bad user experience.
    return false;
  }
  try {
    const response = await httpClient.get<ApiModel.AgentsOnlineResponse>('/api/v1/voice-chat/online-agents');
    return response.data.data.onlineAgents;
  } catch {}
  return false;
}

export async function getActiveConversations({
  onlyActive = true,
  limit = 5,
  offset = 0,
  transactionId,
}: { onlyActive?: boolean; limit?: number; offset?: number; transactionId?: string } = {}) {
  const response = await httpClient.get<ApiModel.ActiveTicketResponse>('/api/v1/support/tickets/self', {
    params: { onlyActive, transactionId },
  });
  throwIfError(response);
  let { data: tickets } = response.data;
  tickets = tickets.sort((a, b) => new Date(b.updatedAt).valueOf() - new Date(a.updatedAt).valueOf());
  const total = tickets.length;
  const paginated = tickets.slice(offset, offset + limit);
  return { tickets: paginated, total };
}

export async function getActiveConversationComments(ticketId: number) {
  const response = await httpClient.get<ApiModel.ActiveTicketCommentsResponse>(
    `/api/v1/support/tickets/${ticketId}/conversations`
  );
  throwIfError(response);
  return response.data;
}

export async function matchOverlayTrigger({
  type,
  id,
  userCountry,
}: {
  type: ChatTriggerMatchSource;
  id: number;
  userCountry?: string;
}) {
  const response = await httpClient.post<ApiModel.ChatOverlayTriggerResponse>(
    '/api/v1/chat/chat-triggers/trigger',
    { type, id },
    { headers: { country: userCountry } }
  );
  throwIfError(response);
  return response?.data?.data?.message ? response?.data?.data : null;
}

matchOverlayTrigger.cached = createCache(matchOverlayTrigger, ({ type, id, userCountry }) =>
  localizeKey(`${type}-${id}-${userCountry}`)
);

export async function trackOverlyClick(payload: {
  type: 'END_CHAT' | 'CONTINUE' | 'OPEN_REQUEST';
  chatTriggerId: number | string;
  ticketCreated?: boolean;
}) {
  await httpClient.post('/api/v1/chat/chat-triggers/trigger/track', payload).catch(() => {});
}

export async function matchTransactionTrigger({
  transactionType,
  transaction,
  userCountry,
  type,
  id,
}: {
  transactionType: TransactionTypeName;
  transaction: TransStatement;
  userCountry?: string;
  type: ChatTriggerMatchSource;
  id: number;
}) {
  const response = await httpClient.post<ApiModel.ChatOverlayTriggerResponse>(
    '/api/v1/chat/chat-triggers/trigger-transaction',
    { selectedTransaction: transactionType, transaction, type, id },
    { headers: { country: userCountry } }
  );
  throwIfError(response);
  return response.data.data;
}

export async function reportChatEnded({ chatId, aiSessionId }: { chatId: number | string; aiSessionId?: string }) {
  const response = await httpClient.post(`/api/v1/chat/customer-end-chat/${chatId}`, { aiSessionId });
  throwIfError(response);
  return response.data.data;
}

export async function getOpenChats() {
  const response = await httpClient.get<ApiModel.ActiveOpenChatResponse>('/api/v1/chat-widget/open');

  throwIfError(response);
  return response.data.data;
}

export async function checkChatCanBeResumed({ chatId }: { chatId: number | string }) {
  const response = await httpClient.get<ApiModel.CheckChatOpenResponse>('/api/v1/chat-widget/check-open', {
    params: { chatId },
  });
  throwIfError(response);
  return response.data.data;
}

/**
 * We only want to report the chat ended once. Caching will make sure that this is reported only once.
 */
reportChatEnded.once = createCache(reportChatEnded, ({ chatId }) => chatId);

export async function getWaitTimes({ chat = true, voice = true }: { chat?: boolean; voice?: boolean } = {}) {
  const response = await httpClient.get<ApiModel.WaitTimeResponse>('/api/v1/chat/waiting-time', {
    params: { chat, voice },
  });
  throwIfError(response);
  return response.data?.data;
}

export async function getActiveConversationConnectSettings({ ticketId }: { ticketId: string | number }) {
  const isMaintenanceOngoing = await looselyCheckIfMaintenanceIsOngoing();
  if (isMaintenanceOngoing) {
    // During maintenance window, we won't be able to provide accurate info about agent availability, and to avoid bad user experience, we will return false for both chat and voice connect.
    return { onlineChatAgentsCheck: false, onlineCallAgentsCheck: false };
  }

  const requestUrl = `/api/v1/chat-widget/online-agents/${`${ticketId}`.trim()}`;

  const response = await httpClient.get<ApiModel.OnlineAgentsCheckResponse>(requestUrl);
  throwIfError(response);
  return response.data?.data;
}

export async function getOnlineAgentsCheck({
  chat = true,
  voice = true,
  ticketId,
}: { chat?: boolean; voice?: boolean; ticketId?: string | number | null } = {}) {
  const isMaintenanceOngoing = await looselyCheckIfMaintenanceIsOngoing();
  if (isMaintenanceOngoing) {
    // During maintenance window, we won't be able to provide accurate info about agent availability, and to avoid bad user experience, we will return false for both chat and voice connect.
    return { onlineChatAgentsCheck: false, onlineCallAgentsCheck: false };
  }

  const requestUrl = '/api/v1/chat/online-agents';
  const params = { chat, voice };

  const response = await httpClient.get<ApiModel.OnlineAgentsCheckResponse>(requestUrl, { params });
  throwIfError(response);
  return response.data?.data;
}

export async function getVoiceAiAvailability() {
  const response = await httpClient.get<ApiModel.VoiceAiAvailabilityResponse>('/api/v1/voice-ai/availability');
  throwIfError(response);

  return response.data?.data;
}

export async function createChatRequest(
  chatRequest: ApiModel.CustomerChatRequest,
  options?: { skipErrorTracking?: boolean }
) {
  const response = await httpClient.post<ApiModel.CustomerChatRequestResponse>(
    '/api/v1/chat/customer-request',
    chatRequest,
    { skipErrorTracking: options?.skipErrorTracking }
  );

  if (response.data.code === ErrorCode.REQUESTS_LIMIT) {
    throw new RequestLimitError();
  }

  throwIfError(response);
  return response.data?.data;
}

export async function createVoiceAiSession() {
  const response = await httpClient.post<ApiModel.VoiceAiNewSessionResponse>('/api/v1/voice-ai/new-chat', {});
  throwIfError(response, true);
  return response.data.data;
}

export async function createVoiceChatRequest(voiceChatRequest: ApiModel.VoiceChatRequest) {
  const response = await httpClient.post<ApiModel.VoiceChatRequestResponse>(
    '/api/v1/voice-chat/request',
    voiceChatRequest
  );

  if (response.data.code === ErrorCode.REQUESTS_LIMIT) {
    throw new RequestLimitError();
  }

  throwIfError(response);
  return response.data?.data;
}

export async function getFeedbackSelections() {
  const response = await httpClient.get<ApiModel.FeedbackCategoryResponse>('/api/v1/satisfaction/feedback-selections');
  throwIfError(response);
  return response.data?.data;
}

getFeedbackSelections.cached = createCache(getFeedbackSelections);

export async function getAiFeedbackSelections() {
  const response = await httpClient.get<ApiModel.FeedbackCategoryResponse>(
    '/api/v1/chat-widget/ai/feedback-selections'
  );
  throwIfError(response);
  return response.data?.data;
}
getAiFeedbackSelections.cached = createCache(getAiFeedbackSelections);

export async function postFeedback({
  rating,
  comment,
  feedbackSelectionIds,
  type,
  chatOrVoiceId,
}: {
  rating: number;
  comment: string;
  feedbackSelectionIds: number[];
  type: ApiModel.ChatRateMode;
  chatOrVoiceId: string | number;
}) {
  let body: {
    rating: number;
    comment: string;
    feedbackSelectionIds: number[];
    aiClickedEndChat?: boolean;
    aiClickedEndChatTime?: string;
  } = {
    rating,
    comment,
    feedbackSelectionIds,
  };

  const typeMap = {
    chat: 'chat',
    voice: 'voice_chat',
    ticket: 'ticket',
    AI: 'ai_chat',
    voiceAi: 'voice_ai_chat',
  };

  if (type === 'AI') {
    body = {
      ...body,
      aiClickedEndChat: true,
      aiClickedEndChatTime: new Date().toISOString(),
    };
  }

  const response = await httpClient.post(`/api/v1/satisfaction/${typeMap[type]}/${chatOrVoiceId}/review/`, body);
  throwIfError(response);
  // return response.data.data;
}

export async function getEntityReview({
  entityId,
  entityType,
}: {
  entityId: number | string;
  entityType: 'chat' | 'voice_chat' | 'ticket';
}) {
  const response = await httpClient.get<ApiModel.EntityReviewResponse>(
    `/api/v1/satisfaction/${entityType}/${entityId}/review`
  );
  throwIfError(response);
  return response.data.data;
}

export async function trackEntitySeen({
  entityId,
  entityType,
}: {
  entityId: number | string;
  entityType: 'chat' | 'voice' | 'ticket';
}) {
  const typeMap = {
    chat: 'chat',
    voice: 'voice_chat',
    ticket: 'ticket',
  };

  await httpClient.post(`/api/v1/satisfaction/${typeMap[entityType]}/${entityId}/track`);
}

export async function wasVoiceChatAccepted({ voiceChatId }: { voiceChatId: number | string }) {
  const response = await httpClient.get<ApiModel.VoiceChatCsatResponse>(
    `/api/v1/satisfaction/voice_chat/${voiceChatId}/show-csat`
  );
  throwIfError(response);
  return response.data.data;
}

// TODO: Move this to a Client Specific file. This depends on the client side config.
export async function checkKycCompleted({
  countryCode,
  sportyToken,
}: {
  countryCode: string;
  sportyToken: string | null | undefined;
}) {
  const response = await httpClient.get<ApiModel.ApiResponse<boolean>>(
    `/api/v1/chat-widget/verify/kyc/${countryCode}`,
    { headers: sportyToken ? { 'sf-access-token': sportyToken } : {} }
  );
  throwIfError(response);
  return response.data.data;
}

export async function getChatWarnings() {
  const response = await httpClient.get<ApiModel.ChatWarningsResponse>('/api/v1/support/end-user-msg/configs');
  throwIfError(response);
  return response.data.data;
}

getChatWarnings.cached = createCache(getChatWarnings);

export async function getMetadataAutomations(params: { articleId: number | string }) {
  if (!params.articleId) {
    logErrorEvents({
      event: 'getMetadataAutomations missing articleId',
      message: 'articleId is required',
      url: '/api/v1/chat-widget/metadata-automation/client',
      data: { articleId: params.articleId },
    });
    return { code: SUCCESS_CODE, message: '', total: 0, data: [] };
  }

  const response = await httpClient.get<ApiModel.MetadataAutomationsResponse>(
    '/api/v1/chat-widget/metadata-automation/client',
    { params }
  );
  throwIfError(response);
  return response.data;
}

getMetadataAutomations.cached = createCache(getMetadataAutomations, ({ articleId }) => localizeKey(articleId));

export async function trackMetadataAutomation(params: Partial<ApiModel.MetadataLog>) {
  const response = await httpClient.post('/api/v1/chat-widget/metadata-automation/track', params);
  throwIfError(response);
}

/**
 * Processes AI chat response and handles different response formats
 */
function processAiChatResponse(originalResponse: string | string[]): string[] {
  if (typeof originalResponse === 'string') {
    return [originalResponse];
  }

  const symbolsToCheck = 4;
  const isParsedResponse =
    originalResponse.length > symbolsToCheck &&
    originalResponse.slice(0, symbolsToCheck).every((x) => x.length === 0 || x.length === 1);

  if (isParsedResponse) {
    const processedResponse = JSON.parse(originalResponse.map((s) => (s === '' ? ' ' : s)).join(''));

    analytics.trackAiEvent({
      name: AI_EVENTS.AI_PARSED_RESPONSE_ISSUE,
      metadata: { response: originalResponse },
    });

    return processedResponse;
  }

  return originalResponse;
}

/**
 * Handles AI-specific errors including threshold limits
 */
function handleAiChatError(error: any): never {
  const thresholdError = localStorage.getItem('test_ai_flow_threshold_error') === 'true';
  if (error.code === ErrorCode.AI_THRESHOLD_LIMIT || thresholdError) {
    // AI TODO: track analytics here
    throw new AiThresholdLimitError();
  }
  throw error;
}

/**
 * Makes AI chat request to the specified endpoint
 */
async function makeAiChatRequest(
  endpoint: string,
  params: ApiModel.GetAiChatResponseParams
): Promise<ApiModel.AiChatResponsePayload> {
  const { first_request, metadata, ...restParams } = params;

  const data = {
    ...restParams,
    ...(metadata && Object.keys(metadata).length > 0
      ? {
          metadata: {
            ...metadata,
            __TZ: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone,
          },
        }
      : {}),
  };

  try {
    const aiResponse = await httpClient.post<ApiModel.AiChatResponse>(endpoint, data, {
      params: { first_request },
      // AI failures are reported via captureEventException (AiFlow / AiResponseError).
      skipErrorTracking: true,
    });

    throwIfError(aiResponse);

    const { response: originalResponse, ...rest } = aiResponse.data.data;
    const processedResponse = processAiChatResponse(originalResponse);

    return { ...rest, response: processedResponse };
  } catch (error) {
    handleAiChatError(error);
  }
}

/**
 * Get AI chat response for regular users
 */
export async function getAiChatResponse(
  params: ApiModel.GetAiChatResponseParams
): Promise<ApiModel.AiChatResponsePayload> {
  return makeAiChatRequest('/api/v1/ai/chat-communication', params);
}

/**
 * Get AI chat response for guest users
 */
export async function getAiChatResponseGuest(
  params: ApiModel.GetAiChatResponseParams
): Promise<ApiModel.AiChatResponsePayload> {
  return makeAiChatRequest('/api/v1/ai/chat-communication-guest', params);
}

export async function trackUserSession(params: ApiModel.SessionLog & { customerId?: string }) {
  const { customerId = '', ...session } = params;

  const headers = {
    type: 'application/json',
  };

  const queryParams = {
    brandSubdomain: httpClient.defaults.headers['brand-subdomain'],
    customerId,
  };

  const response = await httpClient.post('/api/v1/chat-widget/user-track', session, { params: queryParams });
  throwIfError(response);
}

export async function trackUserSessionEvents(params: { events: ApiModel.EventLog[]; customerId?: string }) {
  const queryParams = {
    brandSubdomain: httpClient.defaults.headers['brand-subdomain'],
    customerId: params.customerId,
  };

  const response = await httpClient.post(
    '/api/v1/chat-widget/user-track/events',
    { events: params.events },
    { params: queryParams }
  );
  throwIfError(response);
}

export async function getTransactionMetadata(params: {
  tradeId: string;
  userId: string | number;
  countryCode: string;
}) {
  const response = await httpClient.get<ApiModel.TransactionMetadataResponse>('/api/v1/support/users/transactionInfo', {
    params: {
      uid: params.userId,
      tradeId: params.tradeId,
      countryCode: params.countryCode,
    },
  });
  throwIfError(response);
  return response.data.data;
}

export async function rateChatAiResponse(params: ApiModel.RateAiResponseParams) {
  const response = await httpClient.post('/api/v1/satisfaction/chat-response-feedback', params);
  throwIfError(response);
}

export async function getChatById(chatId: string | number) {
  const response = await httpClient.get<ApiModel.GetChatByIdResponse>(`/api/v1/chat-widget/chat/${chatId}`);
  throwIfError(response);
  return response.data.data;
}

export async function getAiRecentConversation() {
  const response = await httpClient.get('api/v1/ai/recent-conversation');
  throwIfError(response);
  return response.data.data;
}

/**
 * Send AI-related messages / events to BE.
 */
export async function postAiMessages(payload: Record<string, any>) {
  const response = await httpClient.post<ApiModel.ApiResponse<unknown>>('/api/v1/ai/messages', payload);
  throwIfError(response);
  return response.data;
}

export async function saveGuestUserInfo(params: ApiModel.GuestUserInfo) {
  const response = await httpClient.post('/api/v1/ai/guest-user-info', params);
  throwIfError(response);
}

export async function saveGuestLoginToken(params: ApiModel.GuestTokenInfo) {
  const { guestToken, ...payload } = params;
  // We need to send this request with guest token in header
  const response = await httpClient.post('/api/v1/ai/guest-login', payload, {
    headers: { 'pl-access-token': guestToken },
  });
  throwIfError(response);
}

/**
 * Helper function to add locale information to the cache.
 * @param key The key to localize
 */
function localizeKey(key: string | number) {
  return `${key}--lang:${httpClient.defaults.headers['language'] || 'en'}`;
}

// #region Utilities
type Awaited<T> = T extends PromiseLike<infer U> ? { 0: Awaited<U>; 1: U }[U extends PromiseLike<any> ? 0 : 1] : T;

/**
 * Invalidate the cache for the given arguments. If no argument is given, then
 * the entire cache is invalidated.
 */
interface InvalidateCache<P extends any[]> {
  /**
   * Invalidate the cache for the given arguments.
   */
  (...args: P): boolean;
  /**
   * Invalidate entire cache.
   */
  (): boolean;
}

/**
 * A cached version of the callable. The returned objects are cached for further usage.
 * This utility does not cache errors. If errors are needed to be cached, the error needs to be handled in `callable` and returned
 * as a successful response.
 * @see createCache
 */
interface CachedCallable<T extends (...args: any[]) => Promise<any>> {
  /**
   * A cached version of the callable. The returned objects are cached for further usage.
   * This utility does not cache errors. If errors are needed to be cached, the error needs to be handled in `callable` and returned
   * as a successful response.
   * @see createCache
   */
  (...args: Parameters<T>): Promise<Awaited<ReturnType<T>>>;
  /**
   * Invalidate the cache for the given arguments. If no argument is given, then
   * the entire cache is invalidated.
   */
  invalidate: InvalidateCache<Parameters<T>>;
}

/**
 * Create a cached version of the given api. The returned objects are cached for further usage.
 * This utility does not cache errors. If errors are needed to be cached, the error needs to be handled in `callable` and returned
 * as a successful response.
 *
 * NOTE: Modifying the result object will also modify the entry in the cache as the results are shallow copy of the objects returned from
 * the `callable`.
 *
 * NOTE: The tool caches the results of the promise rather than the promise itself.
 * @param callable The callable to create cache for.
 * @param key A function to generate the key from the arguments of the callable. If nothing is passed here, then the results are cached.
 * @returns A promise that resolves with the cached results
 */
function createCache<T extends (...args: any[]) => Promise<any>>(
  callable: T,
  key?: (...args: Parameters<T>) => string | number
): CachedCallable<T> {
  const createKey = key || (() => 'single');
  const memoryStore: Map<string | number, Awaited<ReturnType<T>>> = new Map();
  const cachedCallable = async (...args: Parameters<T>) => {
    const cacheKey = createKey(...args);
    if (memoryStore.has(cacheKey)) {
      return memoryStore.get(cacheKey) as Awaited<ReturnType<T>>;
    }
    try {
      const result: Awaited<ReturnType<T>> = await callable(...args);
      memoryStore.set(cacheKey, result);
      return result;
    } catch (error) {
      throw error;
    }
  };
  function invalidate(): boolean;
  function invalidate(...args: Parameters<T>): boolean;
  function invalidate(...args: Parameters<T>) {
    if (!args.length) {
      memoryStore.clear();
      return true;
    }
    return memoryStore.delete(createKey(...args));
  }
  /**
   * Invalidate cache for the given arguments.
   */
  cachedCallable.invalidate = invalidate;

  return cachedCallable;
}
// #endregion
